import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import dayjs from 'dayjs';
import { Contrato, ContratoService } from 'src/app/contrato';
import { Beneficiaria, BeneficiariaService } from 'src/app/beneficiaria';
import { Breadcrumb, GenerateTableColumn, GenerateTablePageEvent, GenerateTablePagination } from 'src/app/shared/components';
import { catchErr, formatHumanTime } from 'src/app/shared/utils';
import { RelatorioService } from '../relatorio.service';
import { CurrencyPipe } from '@angular/common';
import { dataAtualString, primeiroDiaDoMesString } from 'src/app/shared/date-utils';

export interface VBBFilters {
    beneficiaria?: Beneficiaria,
    contrato?: Contrato,
    contratoId?: string,
    dataInicio?: string,
    dataFim?: string,
    classificacao?: string,
    removePagination?: boolean,
    page?: number,
}
@Component({
    selector: 'app-valor-bruto-beneficiario',
    templateUrl: './valor-bruto-beneficiario.component.html',
    providers: [CurrencyPipe],
    standalone: false
})
export class ValorBrutoBeneficiarioComponent implements OnInit {
    loading: boolean;
    toggleFilters: boolean;
    filters = {} as VBBFilters;
    breadcrumb: Breadcrumb[] = [
        { label: 'Início' },
        { label: 'Relatórios', route: '/relatorio' },
        { label: 'Valores brutos por beneficiários' },
    ]

    contratos: Contrato[];
    classificacoes = ['VEICULOS', 'MAQUINAS']

    dataSource: any[];
    csvData: any[];
    totalizadores: any = {};

    pagination: GenerateTablePagination = { total: 0, current: 0, pageSize: 10 };
    columns: GenerateTableColumn[] = [
        { header: 'N° DIO', attr: 'dioNumero', tdClass: 'text-nowrap' },
        { header: 'Contrato', attr: 'contrato' },
        { header: 'Prefixo', attr: 'prefixo' },
        { header: 'Placa/Série', attr: 'placaSerie' },
        { header: 'Grupo', attr: 'grupo' },
        { header: 'Início Op.', attr: 'dataInicioOperacao' },
        { header: 'Fim Op.', attr: 'dataFimOperacao' },
        { header: 'km', attr: 'totalKmRodados' },
        { header: 'Valor/km', attr: 'valorKmRodado' },
        { header: 'Pgto do km', attr: 'pagamentoKm', thClass: 'text-nowrap' },
        { header: 'Hrs Disponíveis', attr: 'totalHorasDisponiveis' },
        { header: 'Valor/Hr Disponível', attr: 'valorHoraDisponivelVeiculo' },
        { header: 'Pgto/Hr Disponível', attr: 'pagamentoHoraDisponivel' },
        { header: 'Valor Total', attr: 'valorTotalDIO' },
    ];

    constructor(
        private contratoService: ContratoService,
        private beneficiariaService: BeneficiariaService,
        private readonly relatorioService: RelatorioService,
        private readonly currencyPipe: CurrencyPipe,
        private snackBar: MatSnackBar,
    ) { }

    ngOnInit() {
        this.contratoService.getAll<Contrato>().subscribe({
            next: (res) => this.contratos = res.data,
            error: (err) => catchErr(err, this.snackBar)
        });

        this.filters.dataInicio = primeiroDiaDoMesString();
        this.filters.dataFim = dataAtualString();

        this.getAll(this.filters);
    }

    searchFn(term: string, item: any) {
        term = term.toLocaleLowerCase();
        return item.nome.toLocaleLowerCase().indexOf(term) > -1;
    }

    compareWith(item: any, selected: any) {
        return item.id == selected.id
    }

    print() {
        window.print();
    }

    search(pageEvent?: GenerateTablePageEvent) {
        this.loading = true;
        this.pagination.current = pageEvent ? pageEvent.current : this.pagination.current
        this.pagination.pageSize = pageEvent ? pageEvent.pageSize : this.pagination.pageSize
        this.filters.page = pageEvent ? pageEvent.current : this.pagination.current;
        const filters = { ...this.filters };
        filters.dataInicio = filters.dataInicio ? `${dayjs(filters.dataInicio).format('YYYY-MM-DD')}T00:00:00` : filters.dataInicio;
        filters.dataFim = filters.dataFim ? `${dayjs(filters.dataFim).format('YYYY-MM-DD')}T23:59:59` : filters.dataFim;

        this.getAll(filters);
    }

    clear() {
        this.filters = {
            dataInicio: primeiroDiaDoMesString(),
            dataFim: dataAtualString(),
            beneficiaria: undefined,
            contrato: undefined,
            classificacao: undefined
        }
    }

    setBeneficiaria(beneficiaria: Beneficiaria) {
        this.filters.contrato = undefined;
        if (beneficiaria?.id) {
            this.beneficiariaService.getContratos(beneficiaria.id).subscribe({
                next: (res) => this.contratos = res,
                error: (err) => catchErr(err, this.snackBar)
            });
        } else {
            this.contratos = [];
        }
    }

    export(type: string = 'csv') {
      this.loading = true;
      const filters = { ...this.filters, removePagination: true };
      filters.dataInicio = filters.dataInicio ? `${dayjs(filters.dataInicio).format('YYYY-MM-DD')}T00:00:00` : filters.dataInicio;
      filters.dataFim = filters.dataFim ? `${dayjs(filters.dataFim).format('YYYY-MM-DD')}T23:59:59` : filters.dataFim;
      this.relatorioService.downloadArquivoCsvOrPdf(filters, 'dios/valores-brutos', type).subscribe({
          next: (res: any) => {
            const blob = res.body as Blob;
            if (!blob || blob.size === 0) {
                this.snackBar.open('Não foi possível gerar o arquivo. Tente novamente.', '✕', { panelClass: ['error'] });
                this.loading = false;
                return;
            }
            const url = window.URL.createObjectURL(blob);
            const startReport = filters.dataInicio ? dayjs(filters.dataInicio).format('DD-MM-YYYY') : dayjs().format('DD-MM-YYYY')
            const endReport = filters.dataFim ? dayjs(filters.dataFim).format('DD-MM-YYYY') : dayjs().format('DD-MM-YYYY')
            const fileName = `Valores_Brutos_${startReport}_${endReport}`
            const anchor = document.createElement("a");
            anchor.download = `${fileName}.${type}`;
            anchor.href = url;
            anchor.click();
            window.URL.revokeObjectURL(url);
            this.toggleFilters = false;
            this.loading = false;
          }, error: (err) => {
            this.loading = false;
            catchErr(err, this.snackBar)
          }
      });
    }

    private getAll(filters: VBBFilters) {
        this.loading = true;
        this.relatorioService.getValoresBrutos(filters, this.pagination).subscribe({
            next: (res: any) => {
                this.dataSource = this.sanitizeResponse(res.data);
                this.totalizadores = this.calculateTotalizadores(res.data);
                this.pagination = {
                  ...this.pagination,
                  total: res.total,
                }
                this.toggleFilters = false;
                this.loading = false;
            },
            error: (err) => catchErr(err, this.snackBar)
        }).add(() => this.loading = false);
    }

    private calculateTotalizadores(data: any[]) {
        const totalizadores = {
            totalValorDio: 0,
            totalRegistros: data.length,
            totalKmRodados: 0,
            totalValorKmRodado: 0,
            totalHorasDisponiveis: 0,
            totalPagamentoHoraDisponivelVeiculo: 0,
            totalPagamentoHoraDisponivelMaquina: 0,
            equipamentosUtilizados: 0
        };

        // Set para armazenar placas/séries únicas
        const placasUnicas = new Set<string>();

        data.forEach(item => {
            // Converte valores para números para garantir soma numérica
            const totalKmRodados = Number(item.totalKmRodados) || 0;
            const valorKmRodado = Number(item.valorKmRodado) || 0;
            const totalHorasDisponiveis = Number(item.totalHorasDisponiveis) || 0;
            const valorHoraDisponivelVeiculo = Number(item.valorHoraDisponivelVeiculo) || 0;

            // Somatório do valorTotalDIO (valor bruto sem formatação)
            const valorTotalDIO = (totalHorasDisponiveis * valorHoraDisponivelVeiculo) + (valorKmRodado * totalKmRodados);
            totalizadores.totalValorDio += valorTotalDIO;

            // Somatório do kmRodados
            totalizadores.totalKmRodados += totalKmRodados;

            // Somatório do pagamentoKm (valorKmRodado * totalKmRodados)
            totalizadores.totalValorKmRodado += (valorKmRodado * totalKmRodados);

            // Somatório das horas disponíveis
            totalizadores.totalHorasDisponiveis += totalHorasDisponiveis;

            // Somatório do pagamentoHoraDisponivel (totalHorasDisponiveis * valorHoraDisponivelVeiculo)
            totalizadores.totalPagamentoHoraDisponivelVeiculo += (totalHorasDisponiveis * valorHoraDisponivelVeiculo);

            // Adiciona placa/série ao set para contar equipamentos únicos
            if (item.placaSerie) {
                placasUnicas.add(item.placaSerie);
            }
        });

        // Conta equipamentos únicos
        totalizadores.equipamentosUtilizados = placasUnicas.size;

        return totalizadores;
    }

    private sanitizeResponse(data: any[]) {
        return data.map((item: any) => {
            console.log('Item original:', item);
            // Converte valores para números para evitar concatenação de strings
            const totalKmRodados = Number(item.totalKmRodados) || 0;
            const valorKmRodado = Number(item.valorKmRodado) || 0;
            const totalHorasDisponiveis = Number(item.totalHorasDisponiveis) || 0;
            const valorHoraDisponivelVeiculo = Number(item.valorHoraDisponivelVeiculo) || 0;

            return {
                ...item,
                totalKmRodados: Number(totalKmRodados.toFixed(2)), // Formata com no máximo 2 casas decimais
                dataInicioOperacao: item.dataInicioOperacao && dayjs(item.dataInicioOperacao).format('DD/MM/YYYY'),
                dataFimOperacao: item.dataFimOperacao && dayjs(item.dataFimOperacao).format('DD/MM/YYYY'),
                valorKmRodado: this.currencyPipe.transform(valorKmRodado, 'BRL'),
                pagamentoKm: this.currencyPipe.transform(valorKmRodado * totalKmRodados, 'BRL'),
                totalHorasDisponiveis: formatHumanTime(totalHorasDisponiveis),
                valorHoraDisponivelVeiculo: this.currencyPipe.transform(valorHoraDisponivelVeiculo, 'BRL'),
                pagamentoHoraDisponivel: this.currencyPipe.transform(totalHorasDisponiveis * valorHoraDisponivelVeiculo, 'BRL'),
                valorTotalDIO: this.currencyPipe.transform((totalHorasDisponiveis * valorHoraDisponivelVeiculo) + (valorKmRodado * totalKmRodados), 'BRL')
            };
        });
    }
}
